import { useState } from 'react';
import type { FormEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button, ButtonWithLoading } from '@actual-app/components/button';
import { Card } from '@actual-app/components/card';
import { View } from '@actual-app/components/view';

import { FinancialText } from '#components/FinancialText';
import type {
  CommitmentTemplateRequest,
  FinancialManagementState,
  ProductFinanceClient,
} from '#components/product-finance/client';
import {
  formatMoneyInput,
  formatProductDate,
  formatProductMoney,
  parseMoneyInput,
} from '#components/product-finance/money';

import { getManagementErrorMessage } from './management-errors';
import {
  buttonRowStyle,
  errorStyle,
  Field,
  formStyle,
  helpStyle,
  inputStyle,
  ManagementCard,
} from './management-shared';
import {
  createRecurrenceRequest,
  formatRecurrence,
  readFrequency,
} from './recurrence-fields';
import type { RecurrenceFrequency } from './recurrence-fields';

export type ManagedCommitment = FinancialManagementState['commitments'][number];

export function CommitmentManagement({
  state,
  client,
  onChanged,
}: Readonly<{
  state: FinancialManagementState;
  client: ProductFinanceClient;
  onChanged: () => Promise<void>;
}>) {
  const [editing, setEditing] = useState<ManagedCommitment | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  return (
    <View style={{ gap: 14 }}>
      <ManagementCard title={<Trans>Fixed expenses</Trans>}>
        <p style={helpStyle}>
          <Trans>
            Essential expenses protect money in Safe to Spend. Discretionary
            expenses remain planned but can be reconsidered.
          </Trans>
        </p>
        <Button
          variant="primary"
          onPress={() => {
            setEditing(null);
            setIsCreating(true);
          }}
          style={{ alignSelf: 'flex-start', minHeight: 42 }}
        >
          <Trans>Add fixed expense</Trans>
        </Button>
      </ManagementCard>

      {(isCreating || editing !== null) && (
        <CommitmentForm
          key={editing?.template.id ?? 'new'}
          state={state}
          editing={editing}
          client={client}
          onCancel={() => {
            setEditing(null);
            setIsCreating(false);
          }}
          onSaved={async () => {
            setEditing(null);
            setIsCreating(false);
            await onChanged();
          }}
        />
      )}

      {state.commitments.length === 0 ? (
        <ManagementCard title={<Trans>Planned expenses</Trans>}>
          <p style={helpStyle}>
            <Trans>No fixed expenses configured.</Trans>
          </p>
        </ManagementCard>
      ) : (
        <View style={{ display: 'grid', gap: 10 }}>
          {state.commitments.map(commitment => (
            <CommitmentCard
              key={commitment.template.id}
              commitment={commitment}
              client={client}
              onEdit={() => {
                setIsCreating(false);
                setEditing(commitment);
              }}
              onChanged={onChanged}
            />
          ))}
        </View>
      )}
    </View>
  );
}

export function CommitmentCard({
  commitment,
  client,
  onEdit,
  onChanged,
}: Readonly<{
  commitment: ManagedCommitment;
  client: ProductFinanceClient;
  onEdit: () => void;
  onChanged: () => Promise<void>;
}>) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const template = commitment.template;

  async function toggleEnabled() {
    setIsSubmitting(true);
    setError(null);
    try {
      await client.setCommitmentTemplateEnabled({
        id: template.id,
        enabled: !template.enabled,
      });
      await onChanged();
    } catch {
      setError(t('Unable to update this fixed expense.'));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Card style={{ margin: 0 }}>
      <View style={{ padding: 16, gap: 8 }}>
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <View style={{ minWidth: 0 }}>
            <strong>{template.name}</strong>
            <span style={helpStyle}>
              {template.classification === 'essential' ? (
                <Trans>Essential</Trans>
              ) : (
                <Trans>Discretionary</Trans>
              )}{' '}
              · {formatRecurrence(template.recurrence)} ·{' '}
              {template.enabled ? (
                <Trans>Enabled</Trans>
              ) : (
                <Trans>Disabled</Trans>
              )}
            </span>
          </View>
          <FinancialText as="strong">
            {formatProductMoney(template.amount)}
          </FinancialText>
        </View>
        <span style={helpStyle}>
          {commitment.budgetCycle === null ? (
            <Trans>Unassigned to a budget cycle</Trans>
          ) : (
            <Trans>
              Funded by {{ cycleName: commitment.budgetCycle.name }}
            </Trans>
          )}
        </span>
        {commitment.nextOccurrenceDate !== null && (
          <span style={helpStyle}>
            <Trans>
              Next due{' '}
              {{ dueDate: formatProductDate(commitment.nextOccurrenceDate) }}
            </Trans>
            {commitment.fundingDate !== null && (
              <Trans>
                {' '}
                · Funded{' '}
                {{ fundingDate: formatProductDate(commitment.fundingDate) }}
              </Trans>
            )}
          </span>
        )}
        {error !== null && (
          <p role="alert" style={errorStyle}>
            {error}
          </p>
        )}
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          <Button onPress={onEdit} style={{ minHeight: 42 }}>
            <Trans>Edit</Trans>
          </Button>
          <ButtonWithLoading
            isLoading={isSubmitting}
            onPress={() => void toggleEnabled()}
            style={{ minHeight: 42 }}
          >
            {template.enabled ? <Trans>Disable</Trans> : <Trans>Enable</Trans>}
          </ButtonWithLoading>
        </View>
      </View>
    </Card>
  );
}

export function CommitmentForm({
  state,
  editing,
  client,
  onCancel,
  onSaved,
}: Readonly<{
  state: FinancialManagementState;
  editing: ManagedCommitment | null;
  client: ProductFinanceClient;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}>) {
  const { t } = useTranslation();
  const current = editing?.template;
  const recurrence = current?.recurrence;
  const [name, setName] = useState(current?.name ?? '');
  const [amount, setAmount] = useState(
    current === undefined ? '' : formatMoneyInput(current.amount),
  );
  const [frequency, setFrequency] = useState<RecurrenceFrequency>(
    recurrence?.frequency ?? 'monthly',
  );
  const [dayOfWeek, setDayOfWeek] = useState(
    recurrence?.frequency === 'weekly' ? String(recurrence.dayOfWeek) : '1',
  );
  const [dayOfMonth, setDayOfMonth] = useState(
    recurrence?.frequency === 'monthly' || recurrence?.frequency === 'yearly'
      ? String(recurrence.dayOfMonth)
      : '1',
  );
  const [month, setMonth] = useState(
    recurrence?.frequency === 'yearly' ? String(recurrence.month) : '1',
  );
  const [startDate, setStartDate] = useState(current?.startDate ?? '');
  const [endDate, setEndDate] = useState(current?.endDate ?? '');
  const [classification, setClassification] = useState<
    'essential' | 'discretionary'
  >(current?.classification ?? 'essential');
  const [budgetCycleId, setBudgetCycleId] = useState(
    current?.budgetCycleId ?? '',
  );
  const [enabled, setEnabled] = useState(current?.enabled ?? true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const amountMinorUnits = parseMoneyInput(amount);
    if (
      name.trim().length === 0 ||
      amountMinorUnits === null ||
      startDate === ''
    ) {
      setError(t('Enter a name, positive amount, and start date.'));
      return;
    }
    const input: CommitmentTemplateRequest = {
      ...(current === undefined ? {} : { id: current.id }),
      name: name.trim(),
      amountMinorUnits,
      currency: state.settings.currency,
      recurrence: createRecurrenceRequest(
        frequency,
        dayOfWeek,
        dayOfMonth,
        month,
      ),
      startDate,
      ...(endDate === '' ? {} : { endDate }),
      classification,
      ...(budgetCycleId === '' ? {} : { budgetCycleId }),
      enabled,
    };
    setIsSubmitting(true);
    setError(null);
    try {
      if (current === undefined) {
        await client.createCommitmentTemplate(input);
      } else {
        await client.updateCommitmentTemplate(input);
      }
      await onSaved();
    } catch (submitError) {
      setError(getManagementErrorMessage(submitError, t));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <ManagementCard
      title={
        current === undefined ? (
          <Trans>Add fixed expense</Trans>
        ) : (
          <Trans>Edit fixed expense</Trans>
        )
      }
    >
      <form onSubmit={submit} style={formStyle}>
        <Field label={<Trans>Name</Trans>} htmlFor="commitment-name">
          <input
            id="commitment-name"
            value={name}
            onChange={event => setName(event.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field label={<Trans>Amount</Trans>} htmlFor="commitment-amount">
          <input
            id="commitment-amount"
            inputMode="decimal"
            value={amount}
            onChange={event => setAmount(event.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field label={<Trans>Recurrence</Trans>} htmlFor="commitment-frequency">
          <select
            id="commitment-frequency"
            aria-label={t('Recurrence')}
            value={frequency}
            onChange={event => setFrequency(readFrequency(event.target.value))}
            style={inputStyle}
          >
            <option value="weekly">
              <Trans>Weekly</Trans>
            </option>
            <option value="monthly">
              <Trans>Monthly</Trans>
            </option>
            <option value="yearly">
              <Trans>Yearly</Trans>
            </option>
          </select>
        </Field>
        {frequency === 'weekly' && (
          <Field
            label={<Trans>ISO weekday (1–7)</Trans>}
            htmlFor="commitment-weekday"
          >
            <input
              id="commitment-weekday"
              type="number"
              min="1"
              max="7"
              value={dayOfWeek}
              onChange={event => setDayOfWeek(event.target.value)}
              style={inputStyle}
            />
          </Field>
        )}
        {frequency === 'yearly' && (
          <Field label={<Trans>Month (1–12)</Trans>} htmlFor="commitment-month">
            <input
              id="commitment-month"
              type="number"
              min="1"
              max="12"
              value={month}
              onChange={event => setMonth(event.target.value)}
              style={inputStyle}
            />
          </Field>
        )}
        {frequency !== 'weekly' && (
          <Field label={<Trans>Day of month</Trans>} htmlFor="commitment-day">
            <input
              id="commitment-day"
              type="number"
              min="1"
              max="31"
              value={dayOfMonth}
              onChange={event => setDayOfMonth(event.target.value)}
              style={inputStyle}
            />
          </Field>
        )}
        {frequency === 'monthly' && (
          <p style={helpStyle}>
            <Trans>
              Day 31 adjusts to the last valid day of shorter months.
            </Trans>
          </p>
        )}
        <Field label={<Trans>Start date</Trans>} htmlFor="commitment-start">
          <input
            id="commitment-start"
            type="date"
            value={startDate}
            onChange={event => setStartDate(event.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field
          label={<Trans>End date (optional)</Trans>}
          htmlFor="commitment-end"
        >
          <input
            id="commitment-end"
            type="date"
            value={endDate}
            onChange={event => setEndDate(event.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field
          label={<Trans>Classification</Trans>}
          htmlFor="commitment-classification"
        >
          <select
            id="commitment-classification"
            aria-label={t('Classification')}
            value={classification}
            onChange={event =>
              setClassification(readClassification(event.target.value))
            }
            style={inputStyle}
          >
            <option value="essential">
              <Trans>Essential</Trans>
            </option>
            <option value="discretionary">
              <Trans>Discretionary</Trans>
            </option>
          </select>
        </Field>
        <Field label={<Trans>Budget cycle</Trans>} htmlFor="commitment-cycle">
          <select
            id="commitment-cycle"
            value={budgetCycleId}
            onChange={event => setBudgetCycleId(event.target.value)}
            style={inputStyle}
          >
            <option value="">
              <Trans>Unassigned</Trans>
            </option>
            {state.budgetCycles.map(cycle => (
              <option key={cycle.id} value={cycle.id}>
                {cycle.name}
              </option>
            ))}
          </select>
        </Field>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={event => setEnabled(event.target.checked)}
          />
          <Trans>Enabled</Trans>
        </label>
        {error !== null && (
          <p role="alert" style={errorStyle}>
            {error}
          </p>
        )}
        <View style={buttonRowStyle}>
          <ButtonWithLoading
            variant="primary"
            type="submit"
            isLoading={isSubmitting}
            style={{ minHeight: 42 }}
          >
            <Trans>Save fixed expense</Trans>
          </ButtonWithLoading>
          <Button onPress={onCancel} style={{ minHeight: 42 }}>
            <Trans>Cancel</Trans>
          </Button>
        </View>
      </form>
    </ManagementCard>
  );
}

export function readClassification(
  value: string,
): 'essential' | 'discretionary' {
  if (value === 'essential' || value === 'discretionary') {
    return value;
  }
  throw new Error('Unsupported commitment classification.');
}
